import { ListItemIcon, ListItemText, Typography } from '@mui/material';
import MenuItem from '@mui/material/MenuItem';
import { VyIcon } from '@vy/ui-design-system';
import { validateNotNullOrUndefined } from '@vy/utils';
import React from 'react';
import { useQueryClient } from 'react-query';
import { VyDialog } from '../../../../../components/new-design/feedback/VyDialog';
import { useDeleteInvoice } from '../../../../../hooks/requests/use-delete-invoice';
import { useDialog } from '../../../../../hooks/use-dialog';
import { queryKeys } from '../../../../../services/query-key/query-keys';
import { runMutation } from '../../../../../utils/vy-mutation';
import {
  invalidateInvoicesQueries,
  invalidateCustomersQueries,
} from '../../../../../utils/query-invalidation';

type DeleteInvoiceMenuProps = {
  invoiceId?: string;
  invoiceName: string;
  contractName: string;
  disabled?: boolean;
  onSuccessfulRemoval?: () => void;
};

export const DeleteInvoiceMenu: React.FC<DeleteInvoiceMenuProps> = ({
  invoiceId,
  invoiceName,
  contractName,
  disabled,
  onSuccessfulRemoval,
}) => {
  const { isDialogOpen, handleOpen, handleClose } = useDialog();
  const deleteInvoiceMutation = useDeleteInvoice({
    onSuccess: async () => {
      validateNotNullOrUndefined(invoiceId);

      await Promise.all([
        queryClient.invalidateQueries(queryKeys.invoices(invoiceId)),
        invalidateInvoicesQueries(queryClient),
        invalidateCustomersQueries(queryClient),
      ]);

      handleClose();
      onSuccessfulRemoval?.();
    },
  });
  const queryClient = useQueryClient();

  const handleDialogSubmit = async () => {
    handleClose();
    validateNotNullOrUndefined(invoiceId);

    await runMutation(deleteInvoiceMutation, invoiceId, {
      pending: 'Deleting...',
      success: 'Deleted',
      error: (toastProps) => (toastProps?.data as Error)?.message || 'Failed to delete invoice',
    });
  };

  return (
    <>
      <MenuItem disabled={disabled} onClick={handleOpen}>
        <ListItemIcon>
          <VyIcon
            icon="Delete"
            style={{
              color: '#DD0038',
              transform: 'scale(0.8)',
            }}
          />
        </ListItemIcon>
        <ListItemText sx={{ color: '#DD0038' }}>Delete</ListItemText>
      </MenuItem>

      <VyDialog
        open={isDialogOpen}
        onClose={handleClose}
        label={`Delete ${invoiceName} Invoice`}
        cancelButtonText="Cancel"
        ctaText="Yes, delete invoice"
        sx={{
          '& .MuiDialogContent-root': {
            borderBottom: 'none',
          },
        }}
        ctaButtonProps={{
          sx: {
            backgroundColor: '#FF2E57',
            '&:hover': {
              backgroundColor: '#c92041',
            },
          },
        }}
        ctaDisabled={deleteInvoiceMutation.isLoading}
        onSubmit={handleDialogSubmit}
        dialogContentProps={{
          style: {
            maxWidth: '569px',
            minWidth: '569px',
          },
        }}
      >
        <Typography variant="body1">
          Are you sure you want to delete invoice for contract {contractName}? This action is final and cannot
          be reversed.
        </Typography>
      </VyDialog>
    </>
  );
};
